"use client";

import { cn } from "@/lib/utils";
import { COLOR_NAME, COLOR_PICKER } from "@/types/Contentful.model";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Block, BLOCKS, Inline } from "@contentful/rich-text-types";
import { ReactNode } from "react";
import { Typography } from "../Typography/Typography.component";
import { IntroTextWithVideosPropsModel } from "./IntroTextWithVideos.model";

export function IntroTextWithVideos({
  content,
  textColor,
  backgroundColor,
  className,
}: IntroTextWithVideosPropsModel) {
  const options = {
    preserveWhitespace: true,
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
        // Check if paragraph is empty or contains only whitespace
        const textContent = node.content
          .filter((child: any) => child.nodeType === "text")
          .map((child: any) => child.value)
          .join("");

        if (!textContent.trim()) {
          // Force line break for empty paragraphs
          return <div className="w-full" />;
        }

        return (
          <Typography.H1
            className={cn(
              "max-largeMobile:text-[40px] inline max-md:text-[56px]",
              textColor?.name &&
                `text-${COLOR_PICKER[textColor.name as COLOR_NAME]}`,
            )}
          >
            {children}
          </Typography.H1>
        );
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: Block | Inline) => {
        const asset = node.data.target;

        // Check if it's a video asset
        if (asset?.fields?.file?.contentType?.startsWith("video/")) {
          const videoUrl = asset.fields.file.url.startsWith("//")
            ? `https:${asset.fields.file.url}`
            : asset.fields.file.url;

          return (
            <div className="mx-2 inline-block h-13 w-auto overflow-hidden rounded-2xl align-baseline md:mx-4 md:h-20">
              <video
                className="inline-block h-full w-auto"
                key={asset.sys.id}
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={videoUrl} type={asset.fields.file.contentType} />
                Your browser does not support the video tag.
              </video>
            </div>
          );
        }

        // Fallback for non-video assets
        return null;
      },
    },
  };

  return (
    <div
      className={cn(
        "intro-text-with-videos px-4 py-62 md:py-66",
        backgroundColor?.name &&
          `bg-${COLOR_PICKER[backgroundColor.name as COLOR_NAME]}`,
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-center gap-0 text-justify leading-tight">
        {documentToReactComponents(content.json, options)}
      </div>
    </div>
  );
}
